import { FC } from 'react';
import { CUSTOM_THEME, NumberUtils, Tr } from '@utils';
import { useLoggedinUser } from '@hooks';
import { Permission, Stock } from '@services';
import { Grid, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import { VehicleImage } from '@components/portal/common';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type Entity = Stock;

export interface IStockListViewerProps extends IEntityListProps<Entity> {
	paddingX?: number | string;
	entities: Entity[];
	selected: Entity[];
	onSelectChange?: (selected: Entity[]) => void;
	onEdit?: (entity: Entity) => void;
	onDelete?: (entity: Entity) => void;
}

export const StockListViewer: FC<IStockListViewerProps> = ({
	entities,
	selected,
	onSelectChange,
	onEdit,
	onDelete,
	paddingX,
	...props
}) => {
	const user = useLoggedinUser();
	const hasWritePermission = user?.hasPermission(Permission.STOCK_WRITE);
	const handleSelectChange = (status: boolean, entity: Entity) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!hasWritePermission}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => (
				<EntityListItem
					key={`stock-${entity.id}-${i}`}
					sx={{ paddingRight: paddingX }}
					selected={!!selected.find((s) => s.id === entity.id)}
					notSelectable={!hasWritePermission}
					badge={
						<Typography variant="body2">
							<Tr.Portal path={entity.isAvailable ? 'stock.available' : 'stock.out-of-stock'} />
						</Typography>
					}
					badgeSx={{ backgroundColor: entity.isAvailable ? 'primary.main' : CUSTOM_THEME.extraColors.nightBlue }}
					actions={[
						{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !hasWritePermission },
						{ text: <Tr.Portal path="delete" />, onClick: () => onDelete?.(entity), hidden: !hasWritePermission },
						{
							text: <Tr.Portal path="stock.visit-website" />,
							icon: <OpenInNewIcon fontSize="small" sx={{ color: 'primary.main' }} />,
							onClick: () => {
								const link = document.createElement('a');
								link.href = entity.externalLink || '';
								link.target = '_blank';
								document.body.appendChild(link);
								link.click();
								link.remove();
							},
							hidden: !entity.externalLink
						}
					]}
					onSelectChange={(status) => handleSelectChange(status, entity)}
				>
					<Grid container columnSpacing={2}>
						<Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
							<VehicleImage
								model={entity.carModel}
								style={{ width: 'auto', height: 60 }}
								containerProps={{ sx: { height: 1.0 } }}
							/>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="stock.brand" />}>
								<Typography>{entity.carModel?.brand || '-'}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="stock.model" />}>
								<Typography>{entity.carModel?.model || '-'}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path={entity.isAvailable ? 'stock.amount' : 'stock.delivery-time'} />}>
								<Typography>
									{NumberUtils.formatAsNumber(entity.isAvailable ? entity.stockAmount || '0' : entity.buyToOrder || '0')}
								</Typography>
							</EntityListItemCell>
						</Grid>
					</Grid>
				</EntityListItem>
			))}
		</EntityList>
	);
};
